import type { InfiniteData, QueryKey } from "@tanstack/vue-query";
import { computed, type Ref, toRef } from "vue";

import { useNewsletter } from "@/store/stores/account";
import type { GenericModel } from "@/types/model";

import { PUBLIC_API_VERSION } from "../base";
import type { ListingPage } from "./generic";

const fetchCookie = (name: string): string | null => {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]+)")
  );

  return match ? decodeURIComponent(match[1]) : null;
};

export const useNewsletterId = (): Ref<string> => {
  const newsletter = useNewsletter();

  return computed(() => newsletter.value.id);
};

export const useXsrfHeaders = (): Ref<Record<string, string>> => {
  const newsletter = useNewsletter();

  return toRef(() => {
    const apiKey = newsletter.value.api_key;
    const csrfToken = fetchCookie("XSRF-TOKEN");

    const headers: Record<string, string> = {
      "x-api-version": PUBLIC_API_VERSION,
    };

    if (apiKey != null) {
      headers["authorization"] = `Token ${apiKey}`;
    }
    if (csrfToken != null) {
      headers["x-csrftoken"] = csrfToken;
    }

    return headers;
  });
};

export const filterInfiniteQueries = <T extends GenericModel>(
  queries: Array<[QueryKey, InfiniteData<ListingPage<T>> | undefined]>,
  predicate: (item: T) => boolean
): T[] => {
  const found: T[] = [];
  const ids = new Set<string>();

  for (const [, data] of queries) {
    if (!data) {
      continue;
    }

    for (const page of data.pages) {
      for (const item of page.results) {
        if (ids.has(item.id)) {
          continue;
        }

        if (predicate(item)) {
          ids.add(item.id);
          found.push(item);
        }
      }
    }
  }

  return found;
};

export const findItemInListQueries: {
  <T extends GenericModel, S extends T>(
    queries: Array<[QueryKey, T[] | undefined]>,
    predicate: (item: T) => item is S
  ): S | undefined;
  <T extends GenericModel>(
    queries: Array<[QueryKey, T[] | undefined]>,
    predicate: (item: T) => boolean
  ): T | undefined;
} = (
  queries: Array<[QueryKey, GenericModel[] | undefined]>,
  predicate: (item: GenericModel) => boolean
): GenericModel | undefined => {
  for (const [, data] of queries) {
    if (!data) {
      continue;
    }

    for (const item of data) {
      if (predicate(item)) {
        return item;
      }
    }
  }
};

export const findItemInInfiniteQueries = <T extends GenericModel>(
  queries: Array<[QueryKey, InfiniteData<{ results: T[] }> | undefined]>,
  predicate: (item: T) => boolean
): T | undefined => {
  for (const [, data] of queries) {
    if (!data) {
      continue;
    }

    for (const page of data.pages) {
      for (const item of page.results) {
        if (predicate(item)) {
          return item;
        }
      }
    }
  }
};
